import { useEffect, useRef } from 'react';
import { getHackathon } from '../services/hackathon.service';

// the full hackathon is the heaviest read there is, so a page refetches it at most this often, and less often for
// big events (about every 100 s at 1000 people), which keeps a busy kickoff from turning every join into 1000 reads
const MIN_GAP_MS = 10000;
const GAP_PER_PARTICIPANT_MS = 100;
// and spreads the refetches out, so pages open on the same event don't all ask in the same second
const MIN_SPREAD_MS = 3000;
const SPREAD_PER_PARTICIPANT_MS = 20;

/**
 * Keeps an open page's hackathon (ideas, teams, people, categories, winners) current without a reload. The live board,
 * which the page polls anyway, carries a version that changes when any of that does; the page refetches only then.
 */
export const useHackathonSync = (hackathonId: string | undefined, version: string | undefined, participants: number,
    // anything else the page shows from the hackathon (e.g. one idea), refetched at the same moment
    onSync?: () => void) => {
    const onSyncRef = useRef(onSync);
    onSyncRef.current = onSync;
    // the version the page's data matches; the first board seen matches the page's own load
    const synced = useRef<string | undefined>(undefined);
    const lastFetch = useRef(Date.now());
    const timer = useRef(0);

    useEffect(() => {
        if (!hackathonId || !version) {
            return;
        }
        if (synced.current === undefined) {
            synced.current = version;
            return;
        }
        if (version === synced.current) {
            return;
        }
        window.clearTimeout(timer.current);
        const gap = Math.max(MIN_GAP_MS, participants * GAP_PER_PARTICIPANT_MS);
        const spread = Math.max(MIN_SPREAD_MS, participants * SPREAD_PER_PARTICIPANT_MS);
        const wait = Math.max(0, lastFetch.current + gap - Date.now()) + Math.random() * spread;
        timer.current = window.setTimeout(() => {
            synced.current = version;
            lastFetch.current = Date.now();
            getHackathon(hackathonId).catch(err => console.error(err));
            onSyncRef.current && onSyncRef.current();
        }, wait);
    }, [hackathonId, version, participants]);

    useEffect(() => () => window.clearTimeout(timer.current), []);
}
